import { useMemo } from "react";

import type { MetricSummaryData } from "../types";

interface IAverageData {
    Visits?: number | { Value: number };
    PagesPerVisit?: number | { Value: number };
    BounceRate?: number | { Value: number };
    VisitDuration?: number | { Value: number };
    UniqueUsers?: number | { Value: number };
}

/**
 * Value extractor that handles both flat primitives and nested value objects
 *
 * @example
 * extractValue(123) // => 123
 * extractValue({ Value: 123, PreviousPeriodValue: 100, Change: 0.23 }) // => 123
 * extractValue(undefined) // => undefined
 */
const extractValue = <T extends number | { Value: number } | null | undefined>(
    value: T,
): T extends { Value: number } ? number : T extends number ? number : undefined =>
    (value != null && typeof value === "object" && "Value" in value ? value.Value : value) as T extends {
        Value: number;
    }
        ? number
        : T extends number
          ? number
          : undefined;

interface UseMetricSummaryDataParams {
    average: IAverageData | undefined;
    isLoading: boolean;
}

export const useMetricSummaryData = ({
    average,
    isLoading,
}: UseMetricSummaryDataParams): MetricSummaryData | undefined => {
    return useMemo(() => {
        if (!average || isLoading) {
            return undefined;
        }

        const { PagesPerVisit, BounceRate, VisitDuration, Visits, UniqueUsers } = average;

        return {
            ["monthly-visits"]: extractValue(Visits),
            ["pages-per-visit"]: extractValue(PagesPerVisit),
            ["bounce-rate"]: extractValue(BounceRate),
            ["visit-duration"]: extractValue(VisitDuration),
            ["unique-visitors"]: extractValue(UniqueUsers),
        };
    }, [average, isLoading]);
};
